import { reactive } from "vue";
import { get } from "@/api/axiosClient";
import { getActiveProviderSlug } from "./provider";

export const CONFIG_STORAGE_KEY = "theearcasino_app_configuration";

const defaultConfiguration = {
  has_slot: true,
  has_livecasino: false,
  has_instant: false,
  has_crash: false,
  isLoaded: false,
};

const getSavedConfig = () => {
  try {
    const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        has_slot: parsed.has_slot ?? true,
        has_livecasino: Boolean(parsed.has_livecasino),
        has_instant: Boolean(parsed.has_instant),
        has_crash: Boolean(parsed.has_crash),
        isLoaded: true,
      };
    }
  } catch (e) {
    console.error("Error reading saved app configuration:", e);
  }
  return { ...defaultConfiguration };
};

export const appConfigState = reactive(getSavedConfig());

let fetchPromise = null;

export const fetchAppConfiguration = async (force = false) => {
  if (fetchPromise && !force) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const provider = getActiveProviderSlug() || "theearcasino";
      const response = await get(`${provider}/settings/configuration`);
      // console.log("Settings configuration response:", response);
      const data = response?.data || response;
      if (data && typeof data === "object") {
        appConfigState.has_slot = Boolean(data.has_slot);
        appConfigState.has_livecasino = Boolean(data.has_livecasino);
        appConfigState.has_instant = Boolean(data.has_instant);
        appConfigState.has_crash = Boolean(data.has_crash);
        appConfigState.isLoaded = true;

        localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({
          has_slot: appConfigState.has_slot,
          has_livecasino: appConfigState.has_livecasino,
          has_instant: appConfigState.has_instant,
          has_crash: appConfigState.has_crash,
        }));
      }
      return appConfigState;
    } catch (error) {
      console.error("Error fetching settings/configuration:", error);
      return appConfigState;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const isCategoryAllowed = (section) => {
  if (!section) return true;
  const s = section.toLowerCase();
  if (s === "slot") return Boolean(appConfigState.has_slot);
  if (s === "livecasino" || s === "roulette") return Boolean(appConfigState.has_livecasino);
  if (s === "instant") return Boolean(appConfigState.has_instant);
  if (s === "crash") return Boolean(appConfigState.has_crash);
  return true;
};

export const getAllowedCategories = () => {
  const allowed = [];
  if (appConfigState.has_slot) allowed.push("slot");
  if (appConfigState.has_livecasino) allowed.push("livecasino");
  if (appConfigState.has_instant) allowed.push("instant");
  if (appConfigState.has_crash) allowed.push("crash");
  return allowed;
};

export const isSingleCategory = () => {
  return getAllowedCategories().length <= 1;
};

export const getFirstAllowedSection = () => {
  const allowed = getAllowedCategories();
  // Filter only routable sections (slot, livecasino, instant)
  const routable = allowed.filter((s) => s !== "crash");
  return routable[0] || allowed[0] || "slot";
};

export const getUrlCrash = () => {
  const token = localStorage.getItem("botokentheear");
  const bocrash = process.env.VUE_APP_URLBO_CRASHGAME;
  if (!bocrash) return "#";
  return token ? `${bocrash}/verifytoken/${token}` : bocrash;
};
